import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as A from "./lib/api";
import { H1, H2, Sub, Card } from "./Pages";
import { toast } from "sonner";
import { LifeBuoy, KeyRound, ShieldAlert, CheckCircle2, XCircle, Clock, Compass, Layers, ArrowRight } from "lucide-react";

// ==================== DR / CONTINUITY + BREAK-GLASS ====================
export function ContinuityPage() {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [bg, setBg] = useState({ reason: "", confirm_password: "", minutes: 60 });
  const [showBg, setShowBg] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s, b] = await Promise.all([A.dr.continuity(), A.dr.status(), A.dr.breakGlassList()]);
      setCfg({ ...c.data, emergency_operators: (c.data.emergency_operators || []).join(", "), escalation_contacts: (c.data.escalation_contacts || []).join(", ") });
      setStatus(s.data); setSessions(b.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Administrators only"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await A.dr.saveContinuity({
        rto_target: cfg.rto_target, rpo_target: cfg.rpo_target, continuity_notes: cfg.continuity_notes,
        emergency_operators: (cfg.emergency_operators || "").split(",").map((x) => x.trim()).filter(Boolean),
        escalation_contacts: (cfg.escalation_contacts || "").split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Continuity configuration saved"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const requestBg = async () => {
    try {
      await A.dr.breakGlassRequest(bg.reason, bg.confirm_password, Number(bg.minutes));
      toast.success("Break-glass access granted and logged"); setShowBg(false); setBg({ reason: "", confirm_password: "", minutes: 60 }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const closeBg = async (id) => { try { await A.dr.breakGlassClose(id); load(); } catch (e) { toast.error("Failed"); } };

  if (!cfg || !status) return <div>Loading…</div>;
  const readinessChip = status.readiness === "READY" ? "chip-green" : status.readiness === "PARTIAL" ? "chip-amber" : "chip-amber";

  return (
    <div data-testid="continuity-page">
      <H1>Continuity & Break-Glass</H1>
      <Sub>Disaster-recovery readiness and controlled emergency access. Break-glass grants time-boxed, reason-logged access that is fully audited — it never silently bypasses ownership.</Sub>

      <Card className="mb-4" data-testid="dr-status">
        <div className="flex items-center justify-between">
          <div className="label-caps">Continuity readiness</div>
          <span className={`chip ${readinessChip}`} data-testid="dr-readiness">{status.readiness} · {status.ready}/{status.total}</span>
        </div>
        <div className="grid md:grid-cols-2 gap-2 mt-3">
          {status.checks.map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              {c.ok ? <CheckCircle2 size={15} style={{ color: "var(--primary)" }} /> : <XCircle size={15} style={{ color: "var(--overdue)" }} />}
              <span>{c.item}{c.detail ? <span style={{ color: "var(--muted)" }}> — {c.detail}</span> : null}</span>
            </div>
          ))}
        </div>
        {status.active_break_glass > 0 && <div className="banner-warning mt-3 text-sm flex items-center gap-2"><ShieldAlert size={14} /> {status.active_break_glass} active break-glass session(s).</div>}
      </Card>

      <Card className="mb-4">
        <div className="label-caps mb-2 flex items-center gap-1"><LifeBuoy size={14} /> Continuity plan (RTO / RPO)</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><label className="label-caps">RTO target (recovery time)</label><input value={cfg.rto_target} onChange={(e) => setCfg({ ...cfg, rto_target: e.target.value })} placeholder="e.g. 4 hours" data-testid="rto-input" /></div>
          <div><label className="label-caps">RPO target (max data loss)</label><input value={cfg.rpo_target} onChange={(e) => setCfg({ ...cfg, rpo_target: e.target.value })} placeholder="e.g. 24 hours" data-testid="rpo-input" /></div>
          <div><label className="label-caps">Emergency operators (emails, comma-separated)</label><input value={cfg.emergency_operators} onChange={(e) => setCfg({ ...cfg, emergency_operators: e.target.value })} data-testid="operators-input" /></div>
          <div><label className="label-caps">Escalation contacts</label><input value={cfg.escalation_contacts} onChange={(e) => setCfg({ ...cfg, escalation_contacts: e.target.value })} /></div>
          <div className="md:col-span-2"><label className="label-caps">Continuity notes / runbook</label><textarea rows={3} value={cfg.continuity_notes} onChange={(e) => setCfg({ ...cfg, continuity_notes: e.target.value })} /></div>
        </div>
        <button className="btn-primary mt-3" onClick={save} data-testid="dr-save">Save continuity plan</button>
      </Card>

      <Card data-testid="break-glass-card">
        <div className="flex items-center justify-between mb-3">
          <div className="label-caps flex items-center gap-1"><KeyRound size={14} /> Break-glass emergency access</div>
          <button className="btn-gold text-sm flex items-center gap-1" onClick={() => setShowBg(true)} data-testid="break-glass-open"><ShieldAlert size={14} /> Request break-glass</button>
        </div>
        {showBg && (
          <div className="p-3 rounded mb-3" style={{ background: "var(--surface-2)" }} data-testid="break-glass-form">
            <div className="banner-warning text-xs mb-2">Emergency access is time-boxed and permanently logged in the audit trail. Use only when normal access is unavailable.</div>
            <label className="label-caps">Reason (required)</label>
            <textarea rows={2} value={bg.reason} onChange={(e) => setBg({ ...bg, reason: e.target.value })} data-testid="bg-reason" />
            <div className="grid md:grid-cols-2 gap-3 mt-2">
              <div><label className="label-caps">Duration (minutes)</label><input type="number" value={bg.minutes} onChange={(e) => setBg({ ...bg, minutes: e.target.value })} data-testid="bg-minutes" /></div>
              <div><label className="label-caps">Confirm password</label><input type="password" value={bg.confirm_password} onChange={(e) => setBg({ ...bg, confirm_password: e.target.value })} data-testid="bg-password" /></div>
            </div>
            <div className="flex gap-2 mt-3"><button className="btn-primary" onClick={requestBg} data-testid="bg-confirm">Grant access</button><button className="btn-outline" onClick={() => setShowBg(false)}>Cancel</button></div>
          </div>
        )}
        {sessions.length === 0 ? <div className="text-sm" style={{ color: "var(--muted)" }}>No break-glass sessions on record.</div> : (
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.id} className="flex justify-between items-start gap-3 border-b last:border-b-0 py-2" style={{ borderColor: "var(--border)" }} data-testid={`bg-row-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <Clock size={13} /> {s.requested_by}
                    <span className={`chip text-xs ${s.status === "active" ? "chip-amber" : "chip-gray"}`}>{s.status}</span>
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>{s.reason} · granted {(s.granted_at || "").slice(0, 16).replace("T", " ")} · {s.minutes}m</div>
                </div>
                {s.status === "active" && <button className="btn-outline text-xs" onClick={() => closeBg(s.id)} data-testid={`bg-close-${s.id}`}>Close</button>}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ==================== GUIDED / ADVANCED INTERFACE MODE ====================
const GUIDED_TASKS = [
  { to: "/me", label: "See your personal dashboard" },
  { to: "/my-root", label: "Fill in My Root (your private profile)" },
  { to: "/vault", label: "Upload a document to your Secure Vault" },
  { to: "/people", label: "Add beneficiaries & executors" },
  { to: "/legacy-messages", label: "Write a sealed legacy message" },
  { to: "/academy", label: "Start a learning course" },
  { to: "/estate-assistant", label: "Ask the AI Estate Assistant" },
];

export function InterfaceModePage() {
  const [mode, setMode] = useState("advanced");
  const nav = useNavigate();
  useEffect(() => { A.me().then((r) => setMode(r.data.ui_mode || "advanced")).catch(() => {}); }, []);
  const choose = async (m) => {
    try { await A.setUiMode(m); setMode(m); localStorage.setItem("ehu_ui_mode", m); window.dispatchEvent(new Event("ehu-ui-mode")); toast.success(`${m === "guided" ? "Guided" : "Advanced"} mode on`); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <div data-testid="interface-mode-page">
      <H1>Interface Mode</H1>
      <Sub>Choose how much of the portal you see. Guided mode keeps the menu focused on everyday essentials; Advanced shows every register and tool. You can switch any time — it changes only your own view.</Sub>
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Card data-testid="mode-guided" className={mode === "guided" ? "ring-2" : ""} style={mode === "guided" ? { boxShadow: "0 0 0 2px var(--primary)" } : {}}>
          <div className="font-display text-xl flex items-center gap-2" style={{ color: "var(--primary)" }}><Compass size={18} /> Guided</div>
          <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>A focused menu of the pages most people use, with a step-by-step getting-started list. Best for beneficiaries, family members, and new users.</p>
          <button className={mode === "guided" ? "btn-primary mt-3" : "btn-outline mt-3"} onClick={() => choose("guided")} data-testid="choose-guided">{mode === "guided" ? "Current mode" : "Use Guided"}</button>
        </Card>
        <Card data-testid="mode-advanced" className={mode === "advanced" ? "ring-2" : ""} style={mode === "advanced" ? { boxShadow: "0 0 0 2px var(--primary)" } : {}}>
          <div className="font-display text-xl flex items-center gap-2" style={{ color: "var(--primary)" }}><Layers size={18} /> Advanced</div>
          <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>The full portal — every register, studio, governance, and intelligence tool. Best for trustees, administrators, and advisers.</p>
          <button className={mode === "advanced" ? "btn-primary mt-3" : "btn-outline mt-3"} onClick={() => choose("advanced")} data-testid="choose-advanced">{mode === "advanced" ? "Current mode" : "Use Advanced"}</button>
        </Card>
      </div>
      {mode === "guided" && (
        <Card data-testid="guided-tasks">
          <H2>Getting started</H2>
          <div className="space-y-1">
            {GUIDED_TASKS.map((t) => (
              <button key={t.to} onClick={() => nav(t.to)} className="w-full text-left flex items-center justify-between py-2 border-b last:border-b-0 text-sm hover:opacity-80" style={{ borderColor: "var(--border)" }} data-testid={`task-${t.to.slice(1)}`}>
                {t.label} <ArrowRight size={15} style={{ color: "var(--primary)" }} />
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
